import type {
  ItemCard,
  ItemCardSet,
  MemberItemInstance,
} from '../types/pointItemNote';

export type MemberItemCounts = {
  activeUsable: number;
  activeTotal: number;
  passiveOwned: number;
};

function catalogById(
  itemCards: ItemCardSet,
  extraCards?: ItemCard[]
): Map<string, ItemCard> {
  const map = new Map<string, ItemCard>();
  [...itemCards.levelupItem, ...itemCards.assignmentItem, ...itemCards.storeItem].forEach(
    (c) => {
      map.set(c.itemCardId, c);
    }
  );
  (extraCards ?? []).forEach((c) => {
    if (!map.has(c.itemCardId)) map.set(c.itemCardId, c);
  });
  return map;
}

function isActiveUsable(inst: MemberItemInstance, card: ItemCard | undefined): boolean {
  if (inst.isLocked) return false;
  if (card?.type === 'active') {
    return resolveRemainingUseCount(inst, card) > 0;
  }
  return false;
}

/** 인스턴스에 남은 횟수가 없으면 카탈로그 useCount(액티브)로 본다. */
export function resolveRemainingUseCount(
  inst: Pick<MemberItemInstance, 'remainingUseCount'>,
  card: ItemCard | undefined
): number {
  if (typeof inst.remainingUseCount === 'number') return inst.remainingUseCount;
  if (card?.type === 'active') return Math.max(1, card.useCount ?? 1);
  return 0;
}

/** 새 인스턴스 지급 시 저장할 remainingUseCount */
export function remainingUseCountForNewItem(card: ItemCard | undefined): number | undefined {
  if (card?.type !== 'active') return undefined;
  return Math.max(1, card.useCount ?? 1);
}

/** 멤버 보유 아이템: 액티브(사용가능/전체), 패시브(보유 수) */
export function countMemberItems(
  instances: MemberItemInstance[] | undefined,
  memberId: string,
  itemCards: ItemCardSet,
  extraCards?: ItemCard[]
): MemberItemCounts {
  const catalog = catalogById(itemCards, extraCards);
  let activeUsable = 0;
  let activeTotal = 0;
  let passiveOwned = 0;

  for (const inst of instances ?? []) {
    if (inst.memberId !== memberId) continue;
    const card = catalog.get(inst.itemCardId);
    const type = card?.type ?? 'passive';
    if (type === 'active') {
      activeTotal += 1;
      if (isActiveUsable(inst, card)) activeUsable += 1;
    } else {
      if (!inst.isLocked) passiveOwned += 1;
    }
  }

  return { activeUsable, activeTotal, passiveOwned };
}

export function flattenItemCards(itemCards: ItemCardSet): ItemCard[] {
  return [
    ...itemCards.levelupItem,
    ...itemCards.assignmentItem,
    ...itemCards.storeItem,
  ];
}

export function flattenClassPointRewardCards(
  settings: { rewardTiers?: Array<{ cards?: ItemCard[] }> } | null | undefined
): ItemCard[] {
  if (!settings?.rewardTiers?.length) return [];
  return settings.rewardTiers.flatMap((t) => t.cards ?? []);
}

export function findItemCard(
  itemCards: ItemCardSet,
  itemCardId: string,
  extraCards?: ItemCard[]
): ItemCard | undefined {
  return (
    flattenItemCards(itemCards).find((c) => c.itemCardId === itemCardId) ??
    extraCards?.find((c) => c.itemCardId === itemCardId)
  );
}

/** 조회모드용: 잠금 해제된 사용 가능 액티브 아이템 */
export function listUsableActiveItems(
  instances: MemberItemInstance[] | undefined,
  memberId: string,
  itemCards: ItemCardSet,
  extraCards?: ItemCard[]
): Array<{ inst: MemberItemInstance; card: ItemCard | undefined }> {
  const catalog = catalogById(itemCards, extraCards);
  const rows: Array<{ inst: MemberItemInstance; card: ItemCard | undefined }> = [];
  for (const inst of instances ?? []) {
    if (inst.memberId !== memberId) continue;
    const card = catalog.get(inst.itemCardId);
    if (!isActiveUsable(inst, card)) continue;
    rows.push({ inst, card });
  }
  return rows;
}
